import axios from 'axios';
import jwtDecode from 'jwt-decode';
import qs from 'qs';

import { Authorization, User } from '../../app/store/types/auth.types';
import { globalConfig } from '../../configuration/config';
import { getUserFromStorage, getAuthFromStorage } from './auth.service';

export class APICore {
  private baseURL: string;

  constructor() {
    this.baseURL = globalConfig.get().ApplicationUrl;
    axios.defaults.baseURL = this.baseURL;
  }

  /**
   * Sets the base URL for API requests
   * @param url - Base URL to set
   */
  setBaseURL(url: string): void {
    this.baseURL = url;
    axios.defaults.baseURL = this.baseURL;
  }

  setBaseUrl() {
    this.baseURL = globalConfig.get().ApplicationUrl;
    axios.defaults.baseURL = this.baseURL;
  }

  /**
   * Fetches data from given url
   * @param url - API endpoint URL
   * @param params - Query parameters
   */
  get = (url: string, params?: Record<string, any>) => {
    if (!params) {
      return axios.get(url);
    }

    const queryString = Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&');

    return axios.get(`${url}?${queryString}`);
  };

  /**
   * Fetches file from given url
   * @param url - API endpoint URL
   * @param params - Query parameters
   */
  getFile = (url: string, params?: Record<string, any>) => {
    return axios.get(url, {
      params,
      paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      responseType: 'blob',
    });
  };

  /**
   * Fetches data from multiple URLs
   * @param urls - Array of API endpoint URLs
   * @param params - Query parameters to apply to all requests
   */
  getMultiple = (urls: string[], params?: Record<string, any>) => {
    let queryString = '';

    if (params) {
      queryString = Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');
    }

    const requests = urls.map(url => axios.get(`${url}${queryString ? `?${queryString}` : ''}`));

    return axios.all(requests);
  };

  /**
   * Creates a new resource
   * @param url - API endpoint URL
   * @param data - Data to submit
   * @param config - Axios request config
   */
  create = (url: string, data: any, config?: any) => {
    return axios.post(url, data, config);
  };

  /**
   * Updates resource with PATCH
   * @param url - API endpoint URL
   * @param data - Data to update
   */
  updatePatch = (url: string, data: any) => {
    return axios.patch(url, data);
  };

  /**
   * Updates resource with PUT
   * @param url - API endpoint URL
   * @param data - Data to replace
   */
  update = (url: string, data: any) => {
    return axios.put(url, data);
  };

  /**
   * Deletes resource
   * @param url - API endpoint URL
   */
  delete = (url: string) => {
    return axios.delete(url);
  };

  /**
   * Creates resource with file upload
   * @param url - API endpoint URL
   * @param data - Form data
   */
  createWithFile = (url: string, data: Record<string, any>) => {
    const formData = new FormData();

    for (const key in data) {
      formData.append(key, data[key]);
    }

    return axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  /**
   * Updates resource with file upload
   * @param url - API endpoint URL
   * @param data - Form data
   */
  updateWithFile = (url: string, data: Record<string, any>) => {
    const formData = new FormData();

    for (const key in data) {
      formData.append(key, data[key]);
    }

    return axios.patch(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  /**
   * Checks if user is authenticated with valid token
   */
  isUserAuthenticated = (): boolean => {
    const auth = this.getAuthorization();
    if (!auth) return false;

    try {
      const decoded: any = jwtDecode(auth.access_token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp < currentTime) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  };

  /**
   * Sets or clears the logged in user data
   * @param user - User data to store or null to clear
   * @param authorization - Authorization data to store or null to clear
   */
  setLoggedInUser = (user?: User | null, authorization?: Authorization | null) => {
    const AUTH_KEYS = {
      USER: 'current_user',
      ACCESS_TOKEN: 'access_token',
      REFRESH_TOKEN: 'refresh_token',
      EXPIRES_IN: 'expires_in',
    };

    if (user && authorization) {
      sessionStorage.setItem(AUTH_KEYS.USER, JSON.stringify(user));
      sessionStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, authorization.access_token);
      sessionStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, authorization.refresh_token);
      sessionStorage.setItem(AUTH_KEYS.EXPIRES_IN, authorization.expires_in.toString());
    } else {
      sessionStorage.removeItem(AUTH_KEYS.USER);
      sessionStorage.removeItem(AUTH_KEYS.ACCESS_TOKEN);
      sessionStorage.removeItem(AUTH_KEYS.REFRESH_TOKEN);
      sessionStorage.removeItem(AUTH_KEYS.EXPIRES_IN);
    }
  };

  /**
   * Returns the logged in user
   */
  getLoggedInUser = (): User | null => {
    return getUserFromStorage();
  };

  /**
   * Checks if user has administrator role
   */
  isUserAdministrator = (): boolean => {
    if (!this.isUserAuthenticated()) return false;

    const user = this.getLoggedInUser();
    return user?.roles?.includes('administrator') || false;
  };

  /**
   * Checks if user is a demo user
   */
  isDemoUser = (): boolean => {
    if (!this.isUserAuthenticated()) return false;

    const user = this.getLoggedInUser();
    return user?.roles?.includes('PortalKeyUser') || user?.roles?.includes('ApiUser') || false;
  };

  /**
   * Returns the stored authorization data
   */
  getAuthorization = (): Authorization | null => {
    return getAuthFromStorage();
  };

  /**
   * Updates user data in session storage
   * @param modifiedUser - Updated user properties
   */
  setUserInSession = (modifiedUser: Partial<User>): void => {
    const userJson = sessionStorage.getItem('current_user');
    if (!userJson) return;

    try {
      const userData = JSON.parse(userJson);
      const updatedUser = { ...userData, ...modifiedUser };
      sessionStorage.setItem('current_user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Error updating user in session:', error);
    }
  };
}
